import { getRecordingApi } from '@fullstory/browser-api';
import { FsEvent } from '@fullstory/fs-script-api';
import {
  SpecConfig,
  SpecObjectConfig,
  SpecObjectMapping,
  SpecObjectMappingId,
  SpecTargetObjectType,
} from '@fullstory/proto/fs/services/search-settings';
import { useNotifications } from '@fullstory/react-hooks';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../types';
import { useRequestState } from '../../utils/hooks';
import { getOrgId, getUserInfo } from '../fullstory/selectors';
import { SpecActions } from './actions';
import { allMappedSpecObjects, allSpecObjectsBySlug, getFsSpecEnabled } from './selectors';

export const useFetchFsSpec = (shouldFetch: boolean = true) => {
  const fsSpecEnabled = useSelector(getFsSpecEnabled);
  const orgId = useSelector(getOrgId);
  const dispatch = useDispatch<AppDispatch>();

  const fetch = useCallback(async () => {
    if (fsSpecEnabled && shouldFetch) {
      dispatch(SpecActions.listSpecs({ orgId, filter: null }));
      dispatch(SpecActions.listSpecConfigs({ orgId }));
      dispatch(SpecActions.listSpecMappings({ orgId, filter: null })).then(response => {
        if (!window) return;
        if (response.mappings.length > 0) return;

        const FS = getRecordingApi(window);
        if (!FS) return;

        FS('setProperties', {
          type: 'user',
          properties: {
            adopted_spec: true,
          },
        });
      });
      dispatch(SpecActions.listSpecObjectConfigs({ orgId, filter: null }));
    }
  }, [dispatch, orgId, fsSpecEnabled, shouldFetch]);

  useEffect(() => {
    fetch();
  }, [fetch]);
};

type Source = 'MAPPER' | 'MODAL';
type Action = 'UPDATE' | 'DELETE';

export const useFsSpecActions = () => {
  const notifications = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const mappedObjects = useSelector(allMappedSpecObjects);
  const { email } = useSelector(getUserInfo);
  const impersonated = email.includes(' as ');
  const isFullstorian = email.includes('@fullstory.com');

  const updateSpecMapping = useCallback(
    async (mapping: SpecObjectMapping, config?: SpecObjectConfig) => {
      try {
        const response = await dispatch(
          SpecActions.updateSpecObjectMapping({
            orgId,
            mapping,
            retainExistingProperties: false,
            autosuggested: false,
          }),
        );
        if (config) {
          await dispatch(SpecActions.updateSpecMappingConfig({ orgId, config }));
        }
        notifications?.success('Successfully updated role.');
        return response;
      } catch (e) {
        notifications?.error('Failed to update role.');
        return null;
      }
    },
    [dispatch, notifications, orgId],
  );

  const batchUpdateSpecMappings = useCallback(
    async (mappings: SpecObjectMapping[]) => {
      try {
        const response = await dispatch(
          SpecActions.batchUpdateSpecObjectMappings({
            orgId,
            mappings,
            retainExistingProperties: false,
            ignoreIfSkipped: false,
            autosuggested: false,
          }),
        );
        notifications?.success('Successfully updated role.');
        return response;
      } catch (e) {
        notifications?.error('Failed to update role.');
        return null;
      }
    },
    [dispatch, notifications, orgId],
  );

  const updateSpecObjectConfig = useCallback(
    async (config: SpecObjectConfig) => {
      try {
        const response = await dispatch(SpecActions.updateSpecMappingConfig({ orgId, config }));
        notifications?.success('Successfully updated role.');
        return response;
      } catch (e) {
        notifications?.error('Failed to update role.');
        return null;
      }
    },
    [dispatch, notifications, orgId],
  );

  const updateSpecConfig = useCallback(
    async (specConfig: SpecConfig) => {
      try {
        const response = await dispatch(SpecActions.updateSpecConfig({ orgId, specConfig }));
        notifications?.success('Successfully updated specification.');
        return response;
      } catch (e) {
        notifications?.error('Failed to update specification.');
        return null;
      }
    },
    [dispatch, notifications, orgId],
  );

  const deleteFsSpecMapping = useCallback(
    async (mappingId: SpecObjectMappingId | SpecObjectMapping) =>
      dispatch(SpecActions.deleteSpecObjectMapping({ orgId, mappingId })),
    [dispatch, orgId],
  );

  // deletes all active spec mappings given a slug.
  // useful for maintaining 1:1 mappings.
  // we optionally notify, because sometimes we want to do this in the background (like when creating a new mapping)
  const batchDeleteSpecObjectMappingsForSlug = useCallback(
    async (objectSlug: string, notify?: boolean) => {
      try {
        const response = await dispatch(
          SpecActions.batchDeleteSpecObjectMappings({
            orgId,
            filter: {
              objectSlugs: [objectSlug],
              specSlugs: [],
              targetObjectIds: [],
              targetObjectTypes: [],
            },
          }),
        );
        if (notify) notifications?.success('Successfully updated role.');
        return response;
      } catch (e) {
        if (notify) notifications?.error('Failed to update role.');
        return null;
      }
    },
    [dispatch, orgId, notifications],
  );

  const updateObjectWithFsSpec = useCallback(
    async (
      targetObjectId: string,
      targetObjectType: SpecTargetObjectType,
      objectSlugs: string[],
    ) => {
      const currentMappings = mappedObjects.filter(
        object =>
          object.mapping.targetObjectId === targetObjectId &&
          object.mapping.targetObjectType === targetObjectType,
      );

      // delete any slugs that are in the existing mappings but not in the new mappings
      const toDelete = currentMappings.filter(object => !objectSlugs.includes(object.slug));
      await Promise.all(toDelete.map(object => deleteFsSpecMapping(object.mapping)));

      const currentMappingSlugs = currentMappings.map(object => object.slug);
      // create mappings for slugs we haven't already created mappings for
      const slugsToCreate = objectSlugs.filter(slug => !currentMappingSlugs.includes(slug));
      return Promise.all(
        slugsToCreate.map(objectSlug =>
          dispatch(
            SpecActions.updateSpecObjectMapping({
              orgId,
              mapping: {
                objectSlug,
                targetObjectId,
                targetObjectType,
                properties: [],
              },
              retainExistingProperties: false,
              autosuggested: false,
            }),
          ),
        ),
      );
    },
    [dispatch, orgId, mappedObjects, deleteFsSpecMapping],
  );

  // Record a custom event for mapping or property changes.
  const trackSpecMappingUpdate = useCallback(
    async (mapping: SpecObjectMapping, operation: Action, source: Source) => {
      switch (operation) {
        case 'UPDATE':
          if (mapping.properties.length != 0) {
            // Custom event for updated properties
            FsEvent.recordCustomEventV2('Updated Spec Mapping Property', {
              specId: mapping.objectSlug.split('/')[0],
              objectSlug: mapping.objectSlug.split('/')[1],
              orgId,
              targetObjectId: mapping.targetObjectId,
              targetType: mapping.targetObjectType,
              property: mapping.properties[0]?.propertySlug,
              mappingSource: source,
              isFullstorian,
              impersonated,
            });
          } else {
            // Custom event for updated spec object mapping
            FsEvent.recordCustomEventV2('Updated Spec Mappings', {
              specId: mapping.objectSlug.split('/')[0],
              objectSlug: mapping.objectSlug.split('/')[1],
              orgId,
              targetObjectId: mapping.targetObjectId,
              targetType: mapping.targetObjectType,
              mappingSource: source,
              isFullstorian,
              impersonated,
            });
          }
          break;
        case 'DELETE':
          // Custom event for deleting a spec object mapping
          FsEvent.recordCustomEventV2('Deleted Spec Mapping', {
            specId: mapping.objectSlug.split('/')[0],
            objectSlug: mapping.objectSlug.split('/')[1],
            orgId,
            targetObjectId: mapping.targetObjectId,
            targetType: mapping.targetObjectType,
            mappingSource: source,
            isFullstorian,
            impersonated,
          });
      }
    },
    [orgId, impersonated, isFullstorian],
  );

  return {
    updateSpecMapping,
    batchUpdateSpecMappings,
    updateSpecConfig,
    updateSpecObjectConfig,
    batchDeleteSpecObjectMappingsForSlug,
    deleteFsSpecMapping,
    updateObjectWithFsSpec,
    trackSpecMappingUpdate,
  };
};

/**
 * Hook to validate unmapped spec recommendations across different object types
 * Works with metrics, funnels, retentions, and heatmaps
 *
 * @param specs - Array of spec ids to validate, usually extracted from the object
 * @returns Array of unmapped spec objects
 */
export const useUnmappedSpecValidation = (specs: string[]) => {
  const specObjectsBySlug = useSelector(allSpecObjectsBySlug);

  const unmappedSpecs = specs
    .filter(rec => !!rec)
    .filter((rec, index, self) => self.indexOf(rec) === index)
    .filter(rec => {
      const specObj = specObjectsBySlug[rec];
      return specObj && !specObj.mapping;
    })
    .map(rec => specObjectsBySlug[rec]);

  return unmappedSpecs;
};

export const useSegmentRequestState = () =>
  useRequestState(state => state.segments.allSegmentsRequest);
